<template>
    <div class="w-full h-full">
        <FormModal v-if="showFormModal" @cancel="showFormModal = false"
            @confirm="updateAnalytics(); showFormModal = false" title="Edit timeframe"
            subtitle="Edit analytics timeframe" :valid="formStart != '' && formEnd != ''" submitButtonText="Apply">

            <CustomInput v-model="formStart" type="date" label="Start date" />
            <CustomInput v-model="formEnd" type="date" label="End date" />

        </FormModal>
        <Header title="Spend explorer" icon="Telescope" />
        <LoadingPlaceholder
            :loading="!finishedloading"
            :stats="true"
            :charts="true"
            :table="true"
        />
        <SubHeader v-if="finishedloading" title="Merchant Spend Analysis"
            :subText="`View your customers spending behaviour at merchants between ${niceDateTime(startDate)} to ${niceDateTime(endDate)}`">
            >
            <template v-slot:buttons>
                <CustomButton id="createContract" small text="Edit timeframe" type="action" icon="CalendarDays"
                    @click="showFormModal = true" />
            </template>
            <template v-if="topLineData != undefined && retailers.length > 0" v-slot:stats>
                <StatBox name="Spend" currency="£" icon="CreditCard">
                    <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                        <NumberMaker
                            :number="Math.abs(topLineData.spendVolume)"
                            :commas="true"
                            :percentage="false"
                            :decimals="2"
                            currency="£"
                        />
                    </p>
                </StatBox>
                <StatBox name="Transactions" icon="CreditCard">
                    <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                        <NumberMaker
                            :number="Math.abs(topLineData.spendFrequency)"
                            :commas="true"
                            :decimals="0"
                            currency=""
                        />
                    </p>
                </StatBox>
                <StatBox name="Top category by spend" :number="topLineData.topCategory" currency=""
                    icon="PieChart" />
                <StatBox name="Top retailer by spend" :number="retailers[0].retailer.retailerName" currency=""
                    icon="Store" />
            </template>
        </SubHeader>

        <div v-if="finishedloading" class="grid grid-cols-1 lg:grid-cols-2 gap-x-5">
            <div>
                <WhiteBox title="Share of spend">
                    <template v-slot:content>
                        <div>
                            <Doughnut v-if="chartData != undefined" :data="chartData" :options="chartOptions" />
                        </div>
                    </template>
                </WhiteBox>
            </div>
            <WhiteBox title="Categories">
                <template v-slot:content>
                    <EasyDataTable :headers="headersTable2" :items="categorySOW" :loading="!finishedloading"
                        v-model:server-options="serverOptions" :server-items-length="serverItemsLength"
                        :hide-footer="true" :no-hover="true">
                        <template #loading>
                            <div class="p-4 w-full flex justify-center">
                                <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                    fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                            </div>
                        </template>
                        <template #empty-message>
                            <div class="flex justify-center py-4">
                                <div
                                    class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                    <p class="text-sm mr-2">No results</p>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                    </svg>
                                </div>
                            </div>
                        </template>
                        <template #header-action="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #header-transactions="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-transactions="{ transactionCount }">
                            <div class="text-right">
                                <NumberMaker
                                    :number="Math.abs(transactionCount)"
                                    :commas="true"
                                    :decimals="0"
                                    currency=""
                                />  
                            </div>
                        </template>
                        <template #header-spend="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-spend="{ transactionVolume }">
                            <div class="text-right">
                                <NumberMaker
                                    :number="Math.abs(transactionVolume)"
                                    :commas="true"
                                    :decimals="2"
                                    currency="£"
                                />  
                            </div>
                        </template>

                        <template #item-action="retailers">
                            <div class="flex justify-end items-center py-1 gap-1">
                                <CustomButton small text="View category" type="navigate"
                                    @click="$router.push(`/spend-explorer/${retailers.label.replaceAll(' ', '-').toLowerCase()}?start=${this.formStart}&end=${this.formEnd}`)" />
                            </div>
                        </template>
                    </EasyDataTable>
                </template>
            </WhiteBox>
        </div>

        <WhiteBox v-if="finishedloading" title="Top retailers"
            :subText="`View the top retailers your customer spends with between ${niceDateTime(startDate)} to ${niceDateTime(endDate)}`">
            <template v-slot:content>
                <div class="grid mt-6">
                    <SearchBar placeholderText="Search by retailer name"
                        @search="(val) => getRetailerBySearch(0, 10, val)">
                        <CustomButton small text="" type="default" icon="X" @click="this.getRetailers(0, 50)" />
                    </SearchBar>
                </div>
                <EasyDataTable :headers="headersTable1" :items="retailers" :loading="!finishedloading || loadingSearch"
                    v-model:server-options="serverOptions" :server-items-length="serverItemsLength" :hide-footer="true"
                    :no-hover="true">
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                        <div class="flex justify-center py-4">
                            <div
                                class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                <p class="text-sm mr-2">No results</p>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                </svg>
                            </div>
                        </div>
                    </template>
                    <template #header-action="header">
                        <div class="w-full text-right">
                            {{ header.text }}
                        </div>
                    </template>

                    <template #item-retailer="retailers">
                        <LogoAndText :logo="retailers.retailer.logo" :text="retailers.retailer.retailerName" />
                    </template>
                        <template #header-transactions="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-transactions="{ transactionCount }">
                            <div class="text-right">
                                <NumberMaker
                                    :number="Math.abs(transactionCount)"
                                    :commas="true"
                                    :decimals="0"
                                    currency=""
                                />  
                            </div>
                        </template>
                        <template #header-spend="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-spend="{ transactionVolume }">
                            <div class="text-right">
                                <NumberMaker
                                    :number="Math.abs(transactionVolume)"
                                    :commas="true"
                                    :decimals="2"
                                    currency="£"
                                />  
                            </div>
                        </template>
                    <template #item-action="retailers">
                        <div class="flex justify-end items-center py-1 gap-1">
                            <CustomButton small text="View retailer" type="navigate"
                                @click="$router.push(`/retailer/${retailers.retailer.id}?start=${this.formStart}&end=${this.formEnd}`)" />
                        </div>
                    </template>
                </EasyDataTable>
            </template>
        </WhiteBox>
    </div>
</template>

<script setup>
</script>

<script>
import Header from "@/components/Header.vue";
import WhiteBox from "@/components/WhiteBox.vue";
import CustomInput from "@/components/inputs/CustomInput.vue";
import CustomButton from "@/components/inputs/CustomButton.vue";
import FormModal from "@/components/modals/FormModal.vue";
import LogoAndText from "@/components/LogoAndText.vue";
import { DateTime } from 'luxon';
import SearchBar from '@/components/SearchBar.vue'
import StatBox from "@/components/StatBox.vue";
import NumberMaker from "@/components/NumberMaker.vue";
import SubHeader from "@/components/SubHeader.vue";
import { Doughnut } from 'vue-chartjs'

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import LoadingPlaceholder from "@/components/LoadingPlaceholder.vue";

ChartJS.register(ArcElement, Tooltip, Legend)


export default {
    name: "spend-explorer",
    components: {
        Header,
        WhiteBox,
        CustomInput,
        CustomButton,
        FormModal,
        LogoAndText,
        SearchBar,
        StatBox,
        SubHeader,
        Doughnut,
        NumberMaker,
        LoadingPlaceholder
    },
    props: {
    },

    data() {
        return {
            serverOptions: {
                page: 1,
                rowsPerPage: 50,
            },
            topLineData: undefined,

            chartData: undefined,

            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'left'
                    },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                const value = context.raw; // Get the value
                                return `${context.label}: ${value}%`; // Add the % sign
                            },
                        },
                    },
                    title: {
                        display: false,
                        text: 'Transaction count'
                    }
                }
            },
            categorySOW: [],

            serverItemsLength: undefined,
            startDate: '',
            endDate: '',
            formStart: '',
            formEnd: '',
            category: "",
            options: [
                { id: 'Other%20non-essential', text: 'Other non-essential' },
                { id: 'Cafes%20and%20coffee%20shops', text: 'Cafes and coffee shops' },
                { id: 'Pets', text: 'Pets' },
                { id: 'Books%2C%20newsletters%2C%20newspapers', text: 'Books, newsletters, newspapers' },
                { id: 'Food%20and%20Drink', text: 'Food and Drink' },
                { id: 'SaaS%20tools', text: 'SaaS tools' },
                { id: 'Other%20consumer%20services', text: 'Other consumer services' },
                { id: 'Insurance', text: 'Insurance' },
                { id: 'Ridesharing%20and%20taxis', text: 'Ridesharing and taxis' },
                { id: 'Other%20transport', text: 'Other transport' },
                { id: 'Convenience%20stores', text: 'Convenience stores' },
                { id: 'Laundry', text: 'Laundry' },
                { id: 'Groceries', text: 'Groceries' },
                { id: 'Clothing', text: 'Clothing' },
                { id: 'Media', text: 'Media' },
                { id: 'Drugstores%20and%20pharmacies', text: 'Drugstores and pharmacies' },
                { id: 'eCommerce%20purchase', text: 'eCommerce purchase' },
                { id: 'Education', text: 'Education' },
                { id: 'Home%20improvements%20and%20maintenance%20services', text: 'Home improvements and maintenance services' },
                { id: 'Self%20care', text: 'Self care' },
                { id: 'Vehicle%20maintenance', text: 'Vehicle maintenance' },
                { id: 'Taxes', text: 'Taxes' },
                { id: 'Buy%20now%2C%20pay%20later', text: 'Buy now, pay later' },
                { id: 'Medical%20bill', text: 'Medical bill' },
                { id: 'Sport%20and%20fitness', text: 'Sport and fitness' },
                { id: 'Electronics', text: 'Electronics' },
                { id: 'Entertainment%20and%20recreation', text: 'Entertainment and recreation' },
                { id: 'Liquor', text: 'Liquor' },
                { id: 'Utilities', text: 'Utilities' },
                { id: 'Department%20and%20discount%20stores', text: 'Department and discount stores' },
                { id: 'Hotels%20and%20lodging', text: 'Hotels and lodging' },
                { id: 'Gifts', text: 'Gifts' },
                { id: 'Recreational%20goods', text: 'Recreational goods' },
                { id: 'Donation', text: 'Donation' }
            ],

            showErrorModal: false,
            showSuccessModal: false,
            showFormModal: false,
            loading: true,
            loadingSearch: false,
            loadingCategories: true,
            loadingRetailers: true,
            headersTable1: [
                { text: "Retailer", value: "retailer" },
                { text: "Category", value: "retailer.personalisationCategories" },
                { text: "Transactions", value: "transactions" },
                { text: "Spend", value: "spend" },
                { text: "Action", value: "action" },
            ],
            headersTable2: [
                { text: "Category", value: "label" },
                { text: "Transactions", value: "transactions" },
                { text: "Spend", value: "spend" },
                { text: "Action", value: "action" },
            ],
            retailers: [

            ],
        }
    },
    created() {

        if (this.$route.query.start && this.$route.query.end) {
            this.formStart = this.$route.query.start
            this.formEnd = this.$route.query.end
            this.startDate = DateTime.fromFormat(this.formStart,'dd-mm-yyyy').startOf('day');
            this.endDate = DateTime.fromFormat(this.formEnd,'dd-mm-yyyy').endOf('day');


        } else {
            const now = DateTime.local();
            const startOfMonth = now.startOf('month');
            const endOfMonth = now.endOf('month');

            this.startDate = startOfMonth.minus({ month: 2 });
            this.endDate = endOfMonth;
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

        }
        this.getRetailers(0, 50)
        this.getCategories()

    },
    methods: {

        niceDateTime(dateObject) {
            return DateTime.fromISO(dateObject).toLocaleString(DateTime.DATE_SHORT)
        },

        updateAnalytics() {
            this.startDate = DateTime.fromISO(this.formStart).startOf('day');
            this.endDate = DateTime.fromISO(this.formEnd).endOf('day');
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
            this.getRetailers(0, 50)
            this.getCategories()


        },

        async getRetailerBySearch(page, size, search) {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                this.loadingSearch = true

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/share-of-wallet/retailer/popularity/search?page=${page}&size=${size}&startDate=${this.formStart}&endDate=${this.formEnd}&search=${search}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.serverItemsLength = object.length
                    this.retailers = object.content
                    this.loadingSearch = false
                }

            } catch (error) {
                return;
            }

        },

        async getRetailers(page, size) {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                this.loadingSearch = true

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/share-of-wallet/retailer/popularity?page=${page}&size=${size}&startDate=${this.formStart}&endDate=${this.formEnd}${this.category == '' ? '' : '&categories=' + this.category}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.serverItemsLength = object.length
                    this.retailers = object
                    this.loadingRetailers = false
                    this.loadingSearch = false
                }

            } catch (error) {
                return;
            }

        },

        async getCategories() {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                this.loadingCategories = true

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/share-of-wallet/category?startDate=${this.formStart}&endDate=${this.formEnd}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.serverItemsLength = object.length
                    this.categorySOW = object
                    this.loadingCategories = false

                    this.topLineData = {
                        spendVolume: object.reduce((sum, item) => sum + item.transactionVolume, 0),
                        spendFrequency: object.reduce((sum, item) => sum + item.transactionCount, 0),
                        topCategory: object[0].label
                    }

                    this.chartData = {
                        labels: object.map(item => item.label),
                        datasets: [{
                            data: object.map(item => (Math.abs(item.transactionVolume / this.topLineData.spendVolume * 100).toFixed(2)))
                        }]
                    }
                }

            } catch (error) {
                return;
            }

        },

    },
    computed: {
        finishedloading() {
            return this.loadingCategories == false && this.loadingRetailers == false;
        },
    },
    mounted() {
        document.title = "Dashboard | Spend explorer";
    },
    watch: {
        serverOptions: {
            deep: true,
            handler(val) {
                this.getRetailers(val.page - 1, val.rowsPerPage)
            }
        }
    }
}
</script>